import React, {useCallback, useContext} from "react";
import {useDispatch, useSelector} from "react-redux";
import CampaignPanel from "ui/component/crm/panel/CampaignPanel";
import CampaignListTable from "ui/component/crm/table/CampaignListTable";
import GuestListTable from "ui/component/crm/table/GuestListTable";
import {
  closeGeneralSuccessAlert,
  openGeneralSuccessAlert,
} from "redux/actions/settingsActions";
import {getExportCRM} from "redux/actions/crmActions";
import {getExperiences} from "redux/actions/experiencesActions";
import {guestListExportTypes, segmentsCRM} from "configuration/constants";
import {getEncodedFilters} from "utilities/helperFunctions";
import _ from "lodash";
import {useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Container from "connectui/component/layout/Container";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import {useTheme} from "connectui/theme/ThemeProvider";
import StandaloneGuestDetailsPanel from "../component/inbox/panel/guestdetails/StandaloneGuestDetailsPanel";
import {buildStyles} from "../../connectui/component/buildStyles";
import VerticalPanelLayout from "connectui/component/layout/VerticalPanelLayout";
import TextInput from "connectui/component/input/text/type/TextInput";
import OutlinedInputBase from "connectui/component/input/text/OutlinedInputBase";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import Text from "connectui/component/display/Text";
import CrmFiltersPanel from "../component/crm/panel/CrmFiltersPanel";
import {CrmContext} from "../../routes/CrmPage";
import CrmViewsPanel from "../component/crm/panel/CrmViewsPanel";
import OutlinedButtonBase from "../../connectui/component/input/button/base/OutlinedButtonBase";
import ButtonSegmentedGroup from "../../connectui/component/input/button/ButtonSegmentedGroup";
import useResizeObserver from "hooks/useResizeObserver";
import ChipText from "../../connectui/component/display/chip/ChipText";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100%",
      overflow: "auto",
      "&::-webkit-scrollbar": {width: "0!important"},
    },
    outerContainer: {
      backgroundColor: theme.colors.white,
      width: "100%",
      height: "100vh",
      filter: "drop-shadow(0px 1px 2px rgba(10, 13, 18, 0.05))",
      display: "flex",
      flexDirection: "row",
      overflow: "hidden",
    },
  });

export default function CRM() {
  const dispatch = useDispatch(),
    {theme} = useTheme(),
    classes = useStyles(theme),
    mainPanelRef = React.useRef(null),
    {width: panelWidth} = useResizeObserver(mainPanelRef, 100, {
      getInitialWidth: true,
    }),
    isCompact = panelWidth > 0 && panelWidth < 930,
    user_profile = useSelector((state) => state.defaultReducer.user_profile),
    current_user = useSelector((state) => state.defaultReducer.current_user),
    isTabletView =
      useSelector((state) => state.defaultReducer.deviceType) === "tablet",
    [view, setView] = React.useState("guests"),
    [exportMenuAnchorEl, setExportMenuAnchorEl] = React.useState(null),
    [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = React.useState(null),
    [detailsPanel, setDetailsPanel] = React.useState(null),
    [selectedGuest, setSelectedGuest] = React.useState(null),
    [connectedGuests, setConnectedGuests] = React.useState([]),
    [selectedSegment, setSelectedSegment] = React.useState(null),
    [campaigns, setCampaigns] = React.useState([]),
    [selectedCampaign, setSelectedCampaign] = React.useState(null),
    location = useLocation(),
    history = useHistory(),
    fallbackColumns = [
      "guest_name",
      "guest_email",
      "guest_phone",
      "guest_destination",
      "guest_ai_summary",
    ],
    [inputValue, setInputValue] = React.useState("");
  const [showViews, setShowViews] = React.useState(true);

  const {
    appliedFilters,
    setAppliedFilters,
    filtersOpen,
    setFiltersOpen,
    searchText,
    setSearchText,
  } = useContext(CrmContext);

  const {t} = useTranslation();
  const parseFilters = (search) => {
    const params = new URLSearchParams(search);
    const filtersParam = params.get("filters");
    if (!filtersParam) {
      return [];
    }
    try {
      return JSON.parse(decodeURIComponent(filtersParam));
    } catch (e) {
      console.error("Failed to parse filters from URL:", e);
      return [];
    }
  };

  const parseColumns = (search) => {
    const params = new URLSearchParams(search);
    const columnsParam = params.get("columns");

    if (!columnsParam) {
      return fallbackColumns;
    }

    try {
      return JSON.parse(decodeURIComponent(columnsParam));
    } catch (e) {
      console.error("Failed to parse columns from URL:", e);
      return fallbackColumns;
    }
  };

  const filters = React.useMemo(
    () => parseFilters(location.search),
    [location.search],
  );

  React.useEffect(() => {
    setAppliedFilters(filters);
  }, [filters]);

  const columns = React.useMemo(
    () => parseColumns(location.search),
    [location.search],
  );

  const updateFilters = (newFilters) => {
    const params = new URLSearchParams(location.search);
    params.set("filters", encodeURIComponent(JSON.stringify(newFilters)));
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const updateColumns = (newColumns) => {
    const params = new URLSearchParams(location.search);
    params.set("columns", encodeURIComponent(JSON.stringify(newColumns)));
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  React.useEffect(() => {
    if (!current_user) {
      return;
    }
    getCampaigns();
  }, [current_user]);

  React.useEffect(() => {
    if (!selectedSegment) {
      return;
    }
    let missingFilters = !_.isEqual(
      segmentsCRM[selectedSegment.segment_id].filters,
      filters,
    );
    if (missingFilters) {
      setSelectedSegment(null);
    }
  }, [filters, selectedSegment]);

  const getCampaigns = () => {
    dispatch(
      getExperiences({
        type: "cmp",
        onSuccess: (response) => {
          const newCampaigns = response?.experience ?? [];
          setCampaigns(newCampaigns);
        },
      }),
    );
  };

  const openCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setDetailsPanel("inst");
  };

  const handleActionClick = (option, e) => {
    if (option === "export") {
      const el = e.currentTarget;
      setExportMenuAnchorEl(el);
    } else if (option === "inst") {
      setSelectedCampaign(null);
      if (e) {
        setSelectedCampaign(e);
      }
      setDetailsPanel(option);
    } else {
      setDetailsPanel(option);
    }
  };

  const exportReport = (exportType) => {
    setExportMenuAnchorEl(null);
    dispatch(
      getExportCRM({
        interval: "all",
        contentType: exportType,
        query: searchText,
        crmView: "guests",
        filters: getEncodedFilters(filters, false),
        fields: columns,
        onSuccess: (r) => {
          dispatch(
            openGeneralSuccessAlert({
              message: t("crm-guest-data-export-notification"),
              open: true,
              onClose: () => dispatch(closeGeneralSuccessAlert()),
            }),
          );
        },
      }),
    );
  };

  const debouncedSearch = useCallback(
    _.debounce((val) => {
      setSearchText(val);
    }, 1000),
    [],
  );

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const clearSearch = () => {
    setInputValue("");
    debouncedSearch.cancel();
    setSearchText("");
  };

  const clearFilters = () => {
    setInputValue("");
    debouncedSearch.cancel();
    setSearchText("");
    updateFilters([]);
    setAppliedFilters([]);
  };

  React.useEffect(() => {
    setInputValue(searchText);
  }, [searchText]);

  React.useEffect(() => {
    if (view === "campaigns") {
      setFiltersOpen(false);
      setShowViews(false);
    }
  }, [view]);

  const toggleViews = () => {
    setShowViews((prev) => !prev);
    if (!showViews) {
      setFiltersOpen(false);
    }
  };

  const toggleFilters = () => {
    setFiltersOpen((prev) => !prev);
    if (!filtersOpen) {
      setShowViews(false);
    }
  };

  if (
    !Object.keys(user_profile).length ||
    user_profile.scopes?.crm === "hide"
  ) {
    return null;
  }
  return (
    <Container className={classes.outerContainer}>
      <VerticalPanelLayout
        panels={[
          showViews && <CrmViewsPanel onClose={toggleViews} />,
          filtersOpen && (
            <CrmFiltersPanel
              onClose={toggleFilters}
              updateFilters={updateFilters}
            />
          ),
          <Container
            ref={mainPanelRef}
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              height: "100%",
              ...(isTabletView && !!detailsPanel ? {display: "none"} : {}),
            }}
          >
            <ContextMenu
              open={Boolean(exportMenuAnchorEl)}
              anchorEl={exportMenuAnchorEl}
              onClose={() => {
                setExportMenuAnchorEl(null);
              }}
              menuItems={Object.keys(guestListExportTypes).map(
                (exportType) => ({
                  label: t("crm-export-as-menu-content").replace(
                    "{type}",
                    exportType,
                  ),
                  onClick: () => exportReport(exportType),
                }),
              )}
            />

            <FlexContainer
              margin={theme.spacing(4, 4, 4)}
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <FlexContainer flexDirection="column" gap={theme.spacing(1)}>
                <Text variant="displayXS" weight="semibold">
                  Guests
                </Text>
                <Text variant={"textSM"} color={"textSecondary"}>
                  Easily track their bookings, feedback, and preferences in one
                  place.
                </Text>
              </FlexContainer>
              <ButtonIconText
                label={isCompact ? "" : "New Campaign"}
                color={theme.colors.white}
                weight="medium"
                typographyVariant="textSM"
                icon={
                  <SvgIcon
                    component={ICONS.campaign}
                    sx={{
                      marginRight: isCompact ? 0 : theme.spacing(1.5),
                      width: "20px",
                      height: "20px",
                      color: theme.colors.white,
                    }}
                  />
                }
                sx={
                  isCompact
                    ? {
                        padding: theme.spacing(2),
                        minWidth: "unset",
                        width: "fit-content",
                      }
                    : {}
                }
                onClick={() => handleActionClick("inst")}
              />
            </FlexContainer>

            <FlexContainer
              margin={theme.spacing(0, 4, 2)}
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <ButtonSegmentedGroup
                selectedIndex={view === "guests" ? 0 : 1}
                onChange={(index) => {
                  const newView = index === 0 ? "guests" : "campaigns";
                  setView(newView);
                  if (newView === "campaigns") {
                    setFiltersOpen(false);
                    setShowViews(false);
                  }
                }}
              >
                <ButtonIconText
                  buttonBase={OutlinedButtonBase}
                  label={isCompact ? "" : "Guests"}
                  weight="medium"
                  color="textSecondary"
                  typographyVariant="textSM"
                  icon={
                    <SvgIcon
                      component={ICONS.users}
                      sx={{
                        marginRight: isCompact ? 0 : theme.spacing(1.5),
                        width: "20px",
                        height: "20px",
                        color: theme.colors.textSecondary,
                      }}
                    />
                  }
                  sx={
                    isCompact
                      ? {
                          padding: theme.spacing(2),
                          minWidth: "unset",
                          width: "fit-content",
                        }
                      : undefined
                  }
                />
                <ButtonIconText
                  buttonBase={OutlinedButtonBase}
                  label={isCompact ? "" : "Campaigns"}
                  weight="medium"
                  color="textSecondary"
                  typographyVariant="textSM"
                  icon={
                    <SvgIcon
                      component={ICONS.mail}
                      sx={{
                        marginRight: isCompact ? 0 : theme.spacing(1.5),
                        width: "20px",
                        height: "20px",
                        color: theme.colors.textSecondary,
                      }}
                    />
                  }
                  sx={
                    isCompact
                      ? {
                          padding: theme.spacing(2),
                          minWidth: "unset",
                          width: "fit-content",
                        }
                      : undefined
                  }
                />
              </ButtonSegmentedGroup>

              {view === "guests" && (
                <FlexContainer
                  flexDirection={"row"}
                  alignItems={"center"}
                  gap={theme.spacing(2)}
                >
                  <ButtonIconText
                    buttonBase={OutlinedButtonBase}
                    typographyVariant="textSM"
                    weight="medium"
                    color="textSecondary"
                    label={isCompact ? "" : "Views"}
                    onClick={toggleViews}
                    icon={
                      <SvgIcon
                        component={ICONS.menu}
                        sx={{
                          width: "20px",
                          height: "20px",
                          marginRight: isCompact ? 0 : theme.spacing(2),
                          color: theme.colors.textSecondary,
                        }}
                      />
                    }
                    sx={{
                      backgroundColor: showViews
                        ? theme.colors.primary50
                        : "transparent",
                      "&:hover": {
                        backgroundColor: showViews
                          ? theme.colors.primary50
                          : theme.colors.gray100,
                      },
                      ...(isCompact && {
                        padding: theme.spacing(2),
                        minWidth: "unset",
                        width: "fit-content",
                      }),
                    }}
                  />

                  <TextInput
                    inputBase={OutlinedInputBase}
                    startIcon={
                      <SvgIcon
                        component={ICONS.search}
                        sx={{
                          color: theme.colors.textSecondary,
                          width: "20px",
                          height: "20px",
                          marginRight: theme.spacing(1),
                        }}
                      />
                    }
                    inputProps={{
                      placeholder: t("search"),
                      value: inputValue,
                      onChange: (e) => {
                        const val = e.target.value;
                        setInputValue(val);
                        debouncedSearch(val);
                      },
                    }}
                    actions={
                      !!inputValue && (
                        <SvgIcon
                          component={ICONS.x}
                          sx={{
                            width: "11px",
                            height: "11px",
                            cursor: "pointer",
                            color: theme.colors.gray500,
                          }}
                          onClick={clearSearch}
                        />
                      )
                    }
                    sx={{
                      minWidth: isCompact ? "150px" : "250px",
                      maxWidth: isCompact ? "200px" : "350px",
                      padding: theme.spacing(1.5, 2.5),
                    }}
                  />

                  <ButtonIconText
                    buttonBase={OutlinedButtonBase}
                    typographyVariant="textSM"
                    weight="medium"
                    color="textSecondary"
                    label={
                      isCompact ? null : (
                        <FlexContainer
                          alignItems="center"
                          gap={theme.spacing(1.5)}
                        >
                          <Text
                            variant="textSM"
                            color={"textSecondary"}
                            weight="medium"
                          >
                            Filters
                          </Text>
                          {!!appliedFilters.length && (
                            <ChipText
                              text={appliedFilters.length}
                              sx={{
                                backgroundColor: theme.colors.blue50,
                                color: theme.colors.blue700,
                                "&:hover": {
                                  backgroundColor: theme.colors.blue100,
                                },
                              }}
                            />
                          )}
                        </FlexContainer>
                      )
                    }
                    onClick={toggleFilters}
                    icon={
                      <SvgIcon
                        component={ICONS["filter"]}
                        sx={{
                          width: "20px",
                          height: "20px",
                          marginRight: isCompact ? 0 : theme.spacing(2),
                          color: theme.colors.textSecondary,
                        }}
                      />
                    }
                    sx={{
                      backgroundColor: filtersOpen
                        ? theme.colors.primary50
                        : "transparent",
                      "&:hover": {
                        backgroundColor: filtersOpen
                          ? theme.colors.primary50
                          : theme.colors.gray100,
                      },
                      ...(isCompact && {
                        padding: theme.spacing(2),
                        minWidth: "unset",
                        width: "fit-content",
                      }),
                    }}
                  />

                  <OutlinedButtonBase
                    onClick={(e) => {
                      setColumnsMenuAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: theme.spacing(1.5),
                      padding: theme.spacing(2, 3),
                    }}
                  >
                    <SvgIcon
                      component={ICONS.columns}
                      sx={{
                        marginRight: theme.spacing(0.5),
                        width: "20px",
                        height: "20px",
                        color: theme.colors.textSecondary,
                      }}
                    />
                    <Text
                      variant="textSM"
                      color={"textSecondary"}
                      weight="medium"
                    >
                      {t("crm-guest-table-columns-button")}
                    </Text>
                    <SvgIcon
                      component={
                        ICONS[
                          columnsMenuAnchorEl ? "chevron-up" : "chevron-down"
                        ]
                      }
                      sx={{
                        width: "14px",
                        height: "14px",
                        color: theme.colors.textSecondary,
                      }}
                    />
                  </OutlinedButtonBase>

                  <ButtonIconText
                    buttonBase={OutlinedButtonBase}
                    label={isCompact ? "" : "Export"}
                    weight="medium"
                    color="textSecondary"
                    typographyVariant="textSM"
                    onClick={(e) => setExportMenuAnchorEl(e.currentTarget)}
                    icon={
                      <SvgIcon
                        component={ICONS["download"]}
                        sx={{
                          marginRight: isCompact ? 0 : theme.spacing(1.5),
                          width: "20px",
                          height: "20px",
                          color: theme.colors.textSecondary,
                        }}
                      />
                    }
                    sx={
                      isCompact
                        ? {
                            padding: theme.spacing(2),
                            minWidth: "unset",
                            width: "fit-content",
                          }
                        : undefined
                    }
                  />
                </FlexContainer>
              )}
            </FlexContainer>
            {view === "guests" ? (
              <>
                <GuestListTable
                  filters={appliedFilters}
                  searchText={searchText}
                  hideCheckboxColumn={detailsPanel !== "inst"}
                  updateFilters={updateFilters}
                  updateColumns={updateColumns}
                  setSearchText={(newSearch) => {
                    setSearchText(newSearch);
                  }}
                  setSelectedIds={(newIds) => {
                    setConnectedGuests(newIds);
                  }}
                  openGuestPanel={(guest) => {
                    setDetailsPanel("guest_details");
                    setSelectedGuest(guest);
                  }}
                  columns={columns}
                  disableFilters={true}
                  columnsMenuAnchorEl={columnsMenuAnchorEl}
                  setColumnsMenuAnchorEl={setColumnsMenuAnchorEl}
                />
              </>
            ) : (
              <FlexContainer mt={4} flex={1} flexDirection="column">
                <CampaignListTable
                  campaigns={campaigns}
                  onCampaignSelect={openCampaign}
                />
              </FlexContainer>
            )}
          </Container>,

          (!isTabletView || !!detailsPanel) && !!detailsPanel ? (
            detailsPanel === "inst" ? (
              <Container
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CampaignPanel
                  hideContent
                  hideTriggers
                  hideConnection
                  getCampaigns={getCampaigns}
                  closeDetails={() => {
                    setDetailsPanel(null);
                    setSelectedSegment(null);
                  }}
                  selectedCampaign={selectedCampaign}
                  connectedIds={
                    !selectedCampaign
                      ? connectedGuests
                      : (selectedCampaign?.connected_to?.ids ?? [])
                  }
                />
              </Container>
            ) : detailsPanel === "guest_details" ? (
              <StandaloneGuestDetailsPanel
                guestId={selectedGuest.guest_id}
                onClose={() => setDetailsPanel(null)}
              />
            ) : null
          ) : null,
        ]}
        panelStyles={[
          showViews
            ? {minWidth: "330px", maxWidth: "350px"}
            : {display: "none"},
          filtersOpen
            ? {minWidth: "330px", maxWidth: "350px"}
            : {display: "none"},
          {
            flex: isTabletView || !detailsPanel ? 1 : 3,
            minWidth: isTabletView ? "100%" : "500px",
            maxWidth: isTabletView ? "100%" : "none",
          },
          detailsPanel
            ? {
                minWidth: "370px",
                maxWidth: "420px",
              }
            : {display: "none"},
        ]}
        style={{height: "100%", width: "100%"}}
      />
    </Container>
  );
}
